/**
=========================================================
* Trust Cargo - the delivery photo on an order
=========================================================

* One control, used from both places it is wanted: the Operations column of
* the orders list, and inside the order itself.
*
* Attaching the photo is what marks the order RECEIVED, whoever is paying -
* DELIVERD was the same status under a second name, and is gone. There is no
* separate "mark received" - the picture is the proof, and one act does both, so an
* order can never be delivered with no photo or photographed and left sitting
* in the wrong status.
*
* The picture is fetched through the API rather than from /storage/..., which
* on this host is a broken symlink and serves nothing.
*/

import { useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/** 8 MB, the same ceiling the server enforces - refused here so a phone on
 *  a slow connection is not asked to upload it first and be told after. */
const MAX_BYTES = 8 * 1024 * 1024;

function OrderPhoto({ orderId, photoUrl, onChanged, variant }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [error, setError] = useState("");

  const has = Boolean(photoUrl);

  const pick = () => {
    setError("");
    if (file.current) file.current.click();
  };

  const upload = async (e) => {
    const chosen = e.target.files && e.target.files[0];
    // the same input is reused, so clear it or picking the same file twice
    // in a row fires no change event the second time
    e.target.value = "";

    if (!chosen) return;

    if (chosen.size > MAX_BYTES) {
      setError("The picture is over 8 MB");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const body = new FormData();
      body.append("photo", chosen);

      const res = await axios.post(`${API}/orders/${orderId}/photo`, body, {
        headers: { Accept: "application/json" },
      });

      onChanged(res?.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not upload the picture");
    }

    setBusy(false);
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove the delivery photo? The status stays as it is.")) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/orders/${orderId}/photo`);
      setViewing(false);
      onChanged(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the picture");
    }
    setBusy(false);
  };

  /* ------------------------------------------------------------------ */
  /* the row: one small button, like every other row action              */
  /* ------------------------------------------------------------------ */

  const hidden = (
    <input ref={file} type="file" accept="image/*" onChange={upload} style={{ display: "none" }} />
  );

  const viewer = (
    <Dialog open={viewing} onClose={() => setViewing(false)} maxWidth="md">
      <SuiBox sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <SuiBox
          component="img"
          src={photoUrl}
          alt="Delivery"
          sx={{ maxWidth: "100%", maxHeight: "72vh", borderRadius: 1, objectFit: "contain" }}
        />
        <SuiBox sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button size="small" color="error" onClick={remove} disabled={busy}>
            Remove
          </Button>
          <Button size="small" onClick={pick} disabled={busy}>
            Replace
          </Button>
          <Button size="small" variant="contained" onClick={() => setViewing(false)}>
            Close
          </Button>
        </SuiBox>
      </SuiBox>
    </Dialog>
  );

  if (variant === "row") {
    return (
      <>
        {hidden}
        {viewer}
        <Tooltip
          title={
            // eslint-disable-next-line no-nested-ternary
            error ||
            (has ? "Delivery photo - click to view" : "Add the delivery photo (marks it RECEIVED)")
          }
          arrow
        >
          <span>
            <SuiBox
              component="button"
              type="button"
              disabled={busy}
              onClick={has ? () => setViewing(true) : pick}
              aria-label={has ? "View delivery photo" : "Add delivery photo"}
              sx={({ palette, borders, functions: { rgba } }) => {
                // a picture that is there reads green; one that failed reads
                // red; one that was never taken is just another row action
                let tone = "dark";
                if (error) tone = "error";
                else if (has) tone = "success";

                return {
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  cursor: busy ? "wait" : "pointer",
                  border: `1px solid ${rgba(palette[tone].main, 0.28)}`,
                  borderRadius: borders.borderRadius.md,
                  backgroundColor: rgba(palette[tone].main, has ? 0.16 : 0.07),
                  color: palette[tone].main,
                  "&:hover": { backgroundColor: rgba(palette[tone].main, 0.24) },
                };
              }}
            >
              {busy ? (
                <CircularProgress size={15} />
              ) : (
                <Icon sx={{ fontSize: "18px !important" }}>
                  {has ? "photo_camera" : "add_a_photo"}
                </Icon>
              )}
            </SuiBox>
          </span>
        </Tooltip>
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /* inside the order: the picture itself, or a place to drop one        */
  /* ------------------------------------------------------------------ */

  return (
    <SuiBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {hidden}
      {viewer}

      <SuiTypography sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.8 }}>
        Delivery photo
      </SuiTypography>

      <SuiBox
        onClick={has ? () => setViewing(true) : pick}
        sx={({ palette, borders, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          minHeight: 132,
          p: 1,
          cursor: busy ? "wait" : "pointer",
          borderRadius: borders.borderRadius.lg,
          border: `1px dashed ${rgba(palette[error ? "error" : "dark"].main, 0.3)}`,
          backgroundColor: rgba(palette.dark.main, 0.03),
          overflow: "hidden",
          "&:hover": { borderColor: palette.primary.main },
        })}
      >
        {busy && <CircularProgress size={22} />}

        {!busy && has && (
          <SuiBox
            component="img"
            src={photoUrl}
            alt="Delivery"
            sx={{ maxHeight: 118, maxWidth: "100%", borderRadius: 1, objectFit: "contain" }}
          />
        )}

        {!busy && !has && (
          <SuiBox sx={{ textAlign: "center", opacity: 0.75 }}>
            <Icon sx={{ fontSize: "30px !important" }}>add_a_photo</Icon>
            <SuiTypography sx={{ fontSize: 12.5 }}>
              Add the delivery photo - the order becomes RECEIVED
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>

      {error && (
        <SuiTypography sx={({ palette }) => ({ fontSize: 12, color: palette.error.main })}>
          {error}
        </SuiTypography>
      )}
    </SuiBox>
  );
}

OrderPhoto.defaultProps = { photoUrl: null, variant: "row", onChanged: () => {} };

OrderPhoto.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // null when the order has no picture yet
  photoUrl: PropTypes.string,
  onChanged: PropTypes.func,
  // "row" is the icon button in a table; "panel" is the picture in a form
  variant: PropTypes.oneOf(["row", "panel"]),
};

export default OrderPhoto;
